import { BASE_URL } from "./misc";

// One websocket for the dashboard (see the API's realtime/consumers.py).
// Personal events such as `chat.conversation` arrive without subscribing;
// `chat.message` needs a subscription to `chat:<id>`.

const MAX_BACKOFF_MS = 30000;
const PING_INTERVAL_MS = 25000;

class RealtimeClient {
  socket = null;
  listeners = new Map();
  topicCounts = new Map();
  attempts = 0;
  reconnectTimer = null;
  pingTimer = null;
  users = 0;

  /** Keep the socket open while at least one component needs it. */
  retain() {
    this.users += 1;
    if (this.users === 1) this.connect();
    return () => {
      this.users -= 1;
      if (this.users === 0) this.close();
    };
  }

  on(type, handler) {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type).add(handler);
    return () => this.listeners.get(type)?.delete(handler);
  }

  subscribe(topic) {
    const count = this.topicCounts.get(topic) || 0;
    this.topicCounts.set(topic, count + 1);
    if (count === 0) this.send({ action: "subscribe", topic });
    return () => {
      const current = this.topicCounts.get(topic) || 0;
      if (current <= 1) {
        this.topicCounts.delete(topic);
        this.send({ action: "unsubscribe", topic });
      } else {
        this.topicCounts.set(topic, current - 1);
      }
    };
  }

  sendTyping(topic, typing) {
    this.send({ action: "typing", topic, typing });
  }

  connect() {
    if (this.socket) return;
    const token = localStorage.getItem("HOST_TOKEN");
    if (!token) return;
    const url = `${BASE_URL.replace(/^http/, "ws")}ws/v2/realtime/?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.topicCounts.forEach((_count, topic) => this.send({ action: "subscribe", topic }));
      this.pingTimer = setInterval(() => this.send({ action: "ping" }), PING_INTERVAL_MS);
    };
    socket.onmessage = (event) => {
      try {
        const { type, data, topic } = JSON.parse(event.data);
        this.listeners.get(type)?.forEach((handler) => handler(data, topic));
      } catch (e) {
        // ignore malformed frames
      }
    };
    socket.onclose = () => {
      clearInterval(this.pingTimer);
      this.socket = null;
      if (this.users > 0) {
        const delay = Math.min(1000 * 2 ** this.attempts, MAX_BACKOFF_MS);
        this.attempts += 1;
        this.reconnectTimer = setTimeout(() => this.connect(), delay);
      }
    };
  }

  close() {
    clearTimeout(this.reconnectTimer);
    clearInterval(this.pingTimer);
    const socket = this.socket;
    this.socket = null;
    if (socket) socket.close();
  }

  send(payload) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }
}

export const realtime = new RealtimeClient();
